/**
 * A word-level diff, so a review is reading changes rather than hunting them.
 *
 * Two blocks of prose side by side make the reader do the comparison, and the
 * thing most worth noticing is the thing hardest to see: a paragraph that is
 * simply *gone*. A removal leaves no mark on the right-hand side at all, so
 * the eye slides straight past it — which is how a draft that had lost its
 * whole pitch got approved in testing.
 *
 * Longest-common-subsequence over words. Words, not characters, because the
 * unit of meaning here is the word and a character diff on rewritten prose
 * produces confetti. Not a library: this is thirty lines and the alternative
 * is a dependency in the send path's review screen.
 */

export type Part = { text: string; kind: 'same' | 'added' | 'removed' }

/** Split into words while KEEPING the whitespace, so output reassembles exactly. */
function tokenize(text: string): string[] {
  return (text || '').split(/(\s+)/).filter((t) => t !== '')
}

export function diffWords(before: string, after: string): Part[] {
  const a = tokenize(before)
  const b = tokenize(after)

  // Guard: LCS is O(n*m). A 120-word email is nothing, but a pathological
  // input should degrade to "everything changed" rather than hang the tab.
  if (a.length * b.length > 400_000) {
    return [
      { text: before, kind: 'removed' },
      { text: after, kind: 'added' },
    ]
  }

  // lcs[i][j] = length of the longest common subsequence of a[i:] and b[j:]
  const lcs: number[][] = Array.from({ length: a.length + 1 }, () =>
    new Array(b.length + 1).fill(0),
  )
  for (let i = a.length - 1; i >= 0; i--) {
    for (let j = b.length - 1; j >= 0; j--) {
      lcs[i][j] =
        a[i] === b[j] ? lcs[i + 1][j + 1] + 1 : Math.max(lcs[i + 1][j], lcs[i][j + 1])
    }
  }

  const parts: Part[] = []
  const push = (text: string, kind: Part['kind']) => {
    const last = parts[parts.length - 1]
    if (last && last.kind === kind) last.text += text
    else parts.push({ text, kind })
  }

  let i = 0
  let j = 0
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) {
      push(a[i], 'same')
      i++
      j++
    } else if (lcs[i + 1][j] >= lcs[i][j + 1]) {
      push(a[i], 'removed')
      i++
    } else {
      push(b[j], 'added')
      j++
    }
  }
  while (i < a.length) push(a[i++], 'removed')
  while (j < b.length) push(b[j++], 'added')

  return parts
}

/** Whether anything actually changed — "no edit" is a valid, good answer. */
export function unchanged(parts: Part[]): boolean {
  return parts.every((p) => p.kind === 'same' || !p.text.trim())
}
